import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const updateCommentVariablesSchema = z.object({
  id: z.number().or(z.string().transform(Number)),
  comment: z.string().min(1),
});

export type UpdateCommentVariables = z.infer<typeof updateCommentVariablesSchema>;

export async function updateCommentFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateCommentVariables,
) {
  const instance = await axiosInstance;
  return instance.put(`/comments/${data.id}`, { comment: data.comment });
}

export function useUpdateComment() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateCommentVariables>({
    mutationFn: variables => updateCommentFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['comments'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateCommentVariables,
    'updateComment'
  >('updateComment', mutation);
}
